import { motion } from 'framer-motion'
import { FC, useId } from 'react'

interface SparklineProps {
  data: number[]
  color?: string
  width?: number
  height?: number
}

/* Mini gráfico de línea a mano (sin librería), a partir de valores reales (últimos 7 días).
   Incluye un degradado de relleno bajo la línea (color de acento desvaneciéndose a transparente),
   igual al estilo de referencia. useId evita que el <linearGradient> de una card choque con el
   de otra cuando se renderizan varias sparklines a la vez (los ids de un <defs> son globales al DOM). */
const Sparkline: FC<SparklineProps> = ({ data, color = '#4b75f7', width = 64, height = 22 }) => {
  const gradientId = `sparkline-fill-${useId()}`

  if (data.length < 2) return null

  const max = Math.max(...data, 0)
  const min = Math.min(...data, 0)
  const range = max - min || 1
  const stepX = width / (data.length - 1)
  const pad = 2

  const coords = data.map((value, i) => {
    const x = i * stepX
    const y = pad + (1 - (value - min) / range) * (height - pad * 2)
    return { x, y }
  })

  const linePoints = coords.map(({ x, y }) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' ')
  const areaPoints = `0,${height} ${linePoints} ${width},${height}`

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className='overflow-visible shrink-0'>
      <defs>
        <linearGradient id={gradientId} x1='0' y1='0' x2='0' y2='1'>
          <stop offset='0%' stopColor={color} stopOpacity={0.35} />
          <stop offset='100%' stopColor={color} stopOpacity={0} />
        </linearGradient>
      </defs>
      <motion.polygon
        points={areaPoints}
        fill={`url(#${gradientId})`}
        stroke='none'
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5, duration: 0.5, ease: 'easeOut' }}
      />
      <motion.polyline
        points={linePoints}
        fill='none'
        stroke={color}
        strokeWidth={1.5}
        strokeLinecap='round'
        strokeLinejoin='round'
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: 0.7, ease: 'easeOut' }}
      />
    </svg>
  )
}

export default Sparkline
